<template>
  <section class="flex-1 p-4 bg-gray-50 rounded-[4px] relative max-w-[562px]">
    <!-- Title and Info Icon -->
    <div class="flex items-center justify-between mb-4">
      <CText el="p" variant="system-xxs-semibold" class="text-gray-800 uppercase text-[10px] tracking-wide">
        {{ t('climate_rsk.environmental_and_climate_impact') }}
      </CText>
      <div class="flex items-center cursor-pointer" @click="setIsClimateInfoFlyoutVisible(true)">
        <CIcon name="info" size="medium" class="text-primary-900" />
      </div>
    </div>
    <!-- First Part -->
    <div class="flex flex-col gap-y-4">
      <div class="flex flex-row justify-end">
        <div class="flex items-center justify-end w-full text-sm font-semibold bg-gray-100 h-9">
          <span class="text-sm font-semibold">{{ t('climate_rsk.GWP_biogenic_carbon') }}</span>
          <CToggle class="ml-2" :value="displayTotalSection" @change="changeDisplayTotalSection" />
        </div>
      </div>
      <div class="flex flex-row justify-between">
        <CText el="p" class="text-sm font-semibold text-gray-800"> {{ t('climate_rsk.GWP_total_A1_A3') }} </CText>
        <CText el="span" class="text-sm font-normal text-gray-800">
          {{
            getGWPTotal != null
              ? `${formatNumber(getGWPTotal)} ${climateRSK?.unit}`
              : t('climate_rsk.no_data_available')
          }}
        </CText>
      </div>
      <div class="flex flex-col mb-2 ml-2 gap-y-4">
        <div class="flex flex-row justify-between">
          <CText el="p" class="text-sm font-light" :class="[displayTotalSection ? 'text-gray-800' : 'text-gray-400']">
            {{ t('climate_rsk.GWP_biogenic_A1_A3') }}
          </CText>
          <CText
            el="span"
            class="text-sm font-light"
            :class="[displayTotalSection ? 'text-gray-800' : 'text-gray-400']"
          >
            {{
              climateRSK?.biogenic != null
                ? `${formatNumber(climateRSK?.biogenic)} ${climateRSK?.unit}`
                : t('climate_rsk.no_data_available')
            }}
          </CText>
        </div>
        <div class="flex flex-row justify-between">
          <CText el="p" class="text-sm text-gray-800">
            {{ t('climate_rsk.GWP_fossil_A1_A3') }}
          </CText>
          <CText el="span" class="text-sm text-gray-800">
            {{
              climateRSK?.fossil != null
                ? `${formatNumber(climateRSK?.fossil)} ${climateRSK?.unit}`
                : t('climate_rsk.no_data_available')
            }}
          </CText>
        </div>
        <div class="flex flex-row justify-between">
          <CText el="p" class="text-sm text-gray-800">
            {{ t('climate_rsk.GWP_luluc_A1_A3') }}
          </CText>
          <CText el="span" class="text-sm text-gray-800">
            {{
              climateRSK?.luluc != null
                ? `${formatNumber(climateRSK?.luluc)} ${climateRSK?.unit}`
                : t('climate_rsk.no_data_available')
            }}
          </CText>
        </div>
      </div>
      <!-- Second Part -->
      <div class="flex flex-col w-full mt-4 gap-y-4 bottom-4">
        <CText el="p" class="text-gray-800 font-normal uppercase text-[10px] tracking-wide">
          {{ t('climate_rsk.data_quality_and_sources') }}
        </CText>
        <CText el="p" class="flex flex-row items-center text-sm text-gray-800 capitalize gap-x-2">
          {{ climateRSK?.quality }}
          <client-only>
            <NuxtLink
              v-if="document"
              class="flex items-center font-normal cursor-pointer gap-x-2 text-primary-900"
              target="_blank"
              :to="document.assetUrl"
            >
              <CText class="font-normal underline">RSK</CText>
              <CIcon name="open_in_new" />
            </NuxtLink>
          </client-only>
        </CText>
        <span class="flex flex-row items-center text-sm font-normal text-gray-800 no-underline gap-x-2">
          {{ t('climate_rsk.descriptive') }}
        </span>
      </div>
    </div>
    <ClimateInfoFlyout v-if="isClimateInfoFlyoutVisible" @close="setIsClimateInfoFlyoutVisible(false)" />
  </section>
</template>
<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { ClimateRSK } from '@/types/products';

type Props = {
  climateRSK?: ClimateRSK;
  documents?: {
    assetUrl: string;
    assetType: string;
    icon: string;
    assetLabel: string;
  }[];
};

const props = withDefaults(defineProps<Props>(), {
  climateRSK: () => ({} as ClimateRSK),
  documents: () => [],
});

const { t } = useI18n();
const displayTotalSection = ref(false);
const isClimateInfoFlyoutVisible = ref(false);
const changeDisplayTotalSection = (value: boolean) => {
  displayTotalSection.value = value;
};

const getGWPTotal = computed(() => {
  if (!props.climateRSK) return null;

  if (displayTotalSection.value) {
    return props.climateRSK.total;
  }
  return props.climateRSK.total - props.climateRSK.biogenic;
});

const document = computed(() => {
  return props.documents.find((document) => document.assetUrl.includes('/EPD/'));
});

const setIsClimateInfoFlyoutVisible = (val: boolean) => {
  isClimateInfoFlyoutVisible.value = val;
};

const formatNumber = (number: number): string => {
  return number.toLocaleString('en-US', { minimumFractionDigits: 4 }).replace('.', ',');
};
</script>
<script lang="ts">
export default defineComponent({
  name: 'ProductDetailPageClimateLeftSection',
});
</script>
